import { memo, useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { NumericFormat } from 'react-number-format'
import { useDispatch, useSelector } from 'react-redux'
import { useParams } from 'react-router-dom'
import { HugeiconsIcon } from '@hugeicons/react'
import { Note04Icon, NoteAddIcon } from '@hugeicons-pro/core-bulk-rounded'
import {
    Alert,
    Box,
    Button,
    FormControl,
    FormHelperText,
    InputLabel,
    MenuItem,
    Select,
    TextField,
    Typography,
} from '@mui/material'
import PropTypes from 'prop-types'
import { useDebouncedCallback } from 'use-debounce'

import { useGetOrderItemsQuery, useUpdateOrderItemMutation } from '@/app/services/orderItems'
import { useGetOrderQuery } from '@/app/services/orders'
import { selectLocale } from '@/app/slices/appSlice'
import { addItemToUpdateQueue, removeItemFromUpdateQueue, setSelectedOrderStatus } from '@/app/slices/orderItemsSlice'
import {
    selectCurrencyCode,
    selectOrganisationId,
    selectUseImperialUnits,
    selectWebStoreGrainDirectionEnabled,
} from '@/app/slices/organisationSlice'
import { TbxTooltip } from '@/common/components'
import { formatNumber } from '@/common/helpers/formatUtilities'
import { useMutationErrorSnackbar } from '@/common/hooks/useMutationErrorSnackbar'
import { useOrderItemPropertyOptions } from '@/common/hooks/useOrderItemPropertyOptions'
import {
    getCurrencyFormat,
    getPartDimensions,
    getThicknessDisplay,
    getThicknessOptionValue,
    MATERIAL_PROFILE_TYPES,
    ORDER_STATUS_ARRAY,
} from '@/common/utils'

import ItemNotesModal from '../../common/ItemNotesModal'

const classes = {
    ItemDetails_Settings: {
        minWidth: {
            '@0/ItemDetails': '200px',
            '@840/ItemDetails': '220px',
            '@940/ItemDetails': '240px',
            '@1040/ItemDetails': '280px',
        },
        maxWidth: { '@0/OrderItem': '100%', '@940/OrderItem': '280px' },
        gap: 2,
    },
}

const ItemSettings = ({ orderItemId }) => {
    const { t } = useTranslation()
    const { showMutationError } = useMutationErrorSnackbar()
    const { orderId } = useParams()
    const dispatch = useDispatch()

    const organisationId = useSelector(selectOrganisationId)
    const useImperialUnits = useSelector(selectUseImperialUnits)
    const locale = useSelector(selectLocale)
    const currencyCode = useSelector(selectCurrencyCode)
    const showGrainDirection = useSelector(selectWebStoreGrainDirectionEnabled)

    const { data: selectedOrder } = useGetOrderQuery({ organisationId, orderId })
    const { orderItem } = useGetOrderItemsQuery(
        {
            organisationId,
            orderId,
        },
        {
            selectFromResult: ({ data }) => ({
                orderItem: data?.find((item) => item.id === orderItemId),
            }),
        }
    )

    const [updateOrderItem] = useUpdateOrderItemMutation()

    const [itemCuttingTechnology, setItemCuttingTechnology] = useState(orderItem?.cuttingTechnologyId || '')
    const [itemMaterial, setItemMaterial] = useState(orderItem?.materialId || '')
    const [itemThickness, setItemThickness] = useState(orderItem?.thickness || '')
    const [itemWebThickness, setItemWebThickness] = useState(orderItem?.webThickness || '')
    const [itemGrainDirection, setItemGrainDirection] = useState(orderItem?.grainDirection || 'None')
    const [itemQuantity, setItemQuantity] = useState(orderItem?.quantity || 1)
    const [openNotesModal, setOpenNotesModal] = useState(false)
    const [quantityError, setQuantityError] = useState(false)

    const partHasNotes = useMemo(() => {
        const { customerNotes, privateNotes, vendorNotes } = orderItem
        return Boolean(customerNotes) || Boolean(vendorNotes) || Boolean(privateNotes)
    }, [orderItem])

    const partDimensions = useMemo(() => getPartDimensions(orderItem), [orderItem])

    // Composite (webThickness, thickness) Select value so two IBeam records differing only in web
    // are distinct options; a plain String(thickness) for every other profile.
    const thicknessSelectValue = getThicknessOptionValue({
        profile: orderItem?.profile,
        thickness: itemThickness,
        webThickness: itemWebThickness,
    })
    const units = useImperialUnits ? 'in' : 'mm'
    const mode = 'profile' in orderItem ? 'rotary' : 'flat'

    const isPartFromLibrary = Boolean(orderItem?.partLibraryEntryId)
    const currencyFormat = getCurrencyFormat(currencyCode, locale)

    const { cuttingTechOptions, error, grainDirectionOptions, isLoading, materialOptions, thicknessOptions } =
        useOrderItemPropertyOptions({
            cuttingTechnologyId: itemCuttingTechnology,
            materialId: itemMaterial,
            thickness: itemThickness,
            mode,
            partDimensions,
        })

    const moreThanOneCuttingTech = cuttingTechOptions?.length > 1

    // The web/flange hint only makes sense when at least one option actually renders that way.
    const hasIBeamOption = thicknessOptions?.some((option) => option.profile === MATERIAL_PROFILE_TYPES.IBeam.value)

    // QM-3433: a tube (rotary) part whose profile dimensions match no vendor sheet
    // resolves to zero selectable materials. Surface that instead of leaving the
    // user with two unexplained disabled dropdowns.
    const noMatchingMaterial =
        mode === 'rotary' &&
        !isPartFromLibrary &&
        Boolean(itemCuttingTechnology) &&
        !isLoading &&
        !error &&
        materialOptions?.length === 0

    const handleUpdateOrderItem = useCallback(
        async (orderItem) => {
            try {
                // Backend sets the order to NotCalculated as part of the item update (and updateOrderItem
                // refetches the order), so no separate updateOrder PUT is needed here. The optimistic
                // status is already dispatched by the field-change handlers below.
                await updateOrderItem({
                    organisationId,
                    orderId,
                    orderItemId: orderItem?.id,
                    orderItem,
                }).unwrap()

                dispatch(removeItemFromUpdateQueue(orderItemId))
            } catch (error) {
                showMutationError(error, t('Failed to update the order item'))
            }
        },
        [dispatch, showMutationError, orderItemId, orderId, organisationId, t, updateOrderItem]
    )

    const debounceHandleUpdateOrderItem = useDebouncedCallback(handleUpdateOrderItem, 800)

    const disableNotesButton = false
    const showNotesModal = () => {
        setOpenNotesModal(true)
    }
    const hideNotesModal = () => {
        setOpenNotesModal(false)
    }

    const handleCuttingTechChange = async (event) => {
        dispatch(addItemToUpdateQueue(orderItemId))
        if (selectedOrder.status !== ORDER_STATUS_ARRAY.NotCalculated) {
            dispatch(setSelectedOrderStatus(ORDER_STATUS_ARRAY.NotCalculated))
        }

        const value = event?.target?.value
        if (Boolean(value) && itemCuttingTechnology !== value) {
            setItemCuttingTechnology(value)
            setItemMaterial('')
            setItemThickness('')
            setItemWebThickness('')

            await handleUpdateOrderItem({
                ...orderItem,
                cuttingTechnologyId: value,
                materialId: '',
                thickness: '',
                webThickness: '',
                sheetId: '',
                materialConsumptionMode: '',
                isCustomerSuppliedMaterial: '',
            })
        }
    }

    const handleMaterialChange = async (event) => {
        dispatch(addItemToUpdateQueue(orderItemId))
        if (selectedOrder.status !== ORDER_STATUS_ARRAY.NotCalculated) {
            dispatch(setSelectedOrderStatus(ORDER_STATUS_ARRAY.NotCalculated))
        }

        const value = event?.target?.value
        if (Boolean(value) && itemMaterial !== value) {
            setItemMaterial(value)
            setItemThickness('')
            setItemWebThickness('')

            await handleUpdateOrderItem({
                ...orderItem,
                materialId: value,
                thickness: '',
                webThickness: '',
                sheetId: '',
                materialConsumptionMode: '',
                isCustomerSuppliedMaterial: '',
            })
        }
    }

    const handleThicknessChange = async (event) => {
        dispatch(addItemToUpdateQueue(orderItemId))
        if (selectedOrder.status !== ORDER_STATUS_ARRAY.NotCalculated) {
            dispatch(setSelectedOrderStatus(ORDER_STATUS_ARRAY.NotCalculated))
        }
        const value = event?.target?.value
        if (!value || value === thicknessSelectValue) return

        // Resolve the composite Select value back to its (thickness, webThickness) pair.
        // event.target.value must never be written to thickness directly.
        const option = thicknessOptions?.find((o) => o.value === value)
        if (!option) return

        setItemThickness(option.thickness)
        setItemWebThickness(option.webThickness || '')

        await handleUpdateOrderItem({
            ...orderItem,
            thickness: option.thickness,
            webThickness: option.webThickness ?? '',
            sheetId: '',
            materialConsumptionMode: '',
            isCustomerSuppliedMaterial: '',
        })
    }

    const handleGrainDirectionChange = async (event) => {
        dispatch(addItemToUpdateQueue(orderItemId))
        if (selectedOrder.status !== ORDER_STATUS_ARRAY.NotCalculated) {
            dispatch(setSelectedOrderStatus(ORDER_STATUS_ARRAY.NotCalculated))
        }

        const value = event?.target?.value
        if (Boolean(value) && itemGrainDirection !== value) {
            setItemGrainDirection(value)
            await handleUpdateOrderItem({
                ...orderItem,
                grainDirection: value,
            })
        }
    }

    const handleQuantityChange = async (values) => {
        dispatch(addItemToUpdateQueue(orderItemId))
        debounceHandleUpdateOrderItem.cancel()
        if (selectedOrder.status !== ORDER_STATUS_ARRAY.NotCalculated) {
            dispatch(setSelectedOrderStatus(ORDER_STATUS_ARRAY.NotCalculated))
        }

        const { floatValue } = values
        setQuantityError(!floatValue || isNaN(floatValue) || floatValue <= 0)

        setItemQuantity(floatValue)

        if (!isNaN(floatValue) && floatValue >= 1) {
            debounceHandleUpdateOrderItem({
                ...orderItem,
                quantity: floatValue,
            })
        }
    }

    if (error) {
        return <Typography color="error">{t('Error loading settings. Please try again.')}</Typography>
    }

    return (
        <Box
            sx={[
                {
                    alignItems: 'flex-start',
                    display: 'flex',
                    flex: 1,
                    flexDirection: 'column',
                    justifyContent: 'space-around',
                },
                classes.ItemDetails_Settings,
            ]}
        >
            <Box
                sx={{
                    alignItems: 'center',
                    display: 'flex',
                    flexDirection: 'row',
                    justifyContent: 'space-between',
                    width: '100%',
                }}
            >
                <Typography
                    data-testid="order-item-settings-section-title"
                    variant="strong1"
                >
                    {t('Settings')}
                </Typography>

                <Button
                    color="secondary"
                    data-testid="order-item-notes-button"
                    disabled={disableNotesButton}
                    size="small"
                    startIcon={
                        <HugeiconsIcon
                            altIcon={Note04Icon}
                            icon={NoteAddIcon}
                            showAlt={partHasNotes}
                            size={18}
                        />
                    }
                    variant="text"
                    onClick={showNotesModal}
                >
                    {t('Part notes')}
                </Button>
            </Box>

            {/* CUTTING TECH */}
            <Box
                sx={{
                    alignItems: 'center',
                    display: 'flex',
                    flexDirection: 'row',
                    justifyContent: 'space-between',
                    width: '100%',
                }}
            >
                <TbxTooltip
                    title={isPartFromLibrary ? t('Cannot edit parts from library') : ''}
                    arrow
                >
                    <FormControl
                        color="primary"
                        size="small"
                        fullWidth
                    >
                        <InputLabel id="select-itemCuttingTechnology-label">{t('Cutting technology')}</InputLabel>
                        <Select
                            id="select-itemCuttingTechnology"
                            inputProps={{ 'data-testid': 'order-item-cutting-technology' }}
                            label={t('Cutting technology')}
                            labelId="select-itemCuttingTechnology-label"
                            readOnly={!moreThanOneCuttingTech || isPartFromLibrary}
                            value={itemCuttingTechnology}
                            onChange={handleCuttingTechChange}
                        >
                            {cuttingTechOptions
                                ? cuttingTechOptions?.map((ct) => (
                                      <MenuItem
                                          key={ct.cuttingTechnologyId}
                                          value={ct.cuttingTechnologyId}
                                      >
                                          {ct.name}
                                      </MenuItem>
                                  ))
                                : null}
                        </Select>
                    </FormControl>
                </TbxTooltip>
            </Box>

            {noMatchingMaterial ? (
                <Alert
                    severity="warning"
                    sx={{ width: '100%' }}
                >
                    {t(
                        "There isn't any material that matches this part. Please contact us to get this quoted separately."
                    )}
                </Alert>
            ) : (
                <>
                    {/* MATERIAL */}
                    <Box
                        sx={{
                            alignItems: 'center',
                            display: 'flex',
                            flexDirection: 'row',
                            justifyContent: 'space-between',
                            width: '100%',
                        }}
                    >
                        <TbxTooltip
                            title={
                                isPartFromLibrary
                                    ? itemMaterial == '' || itemThickness == ''
                                        ? t('Material for Part Library Part is no longer available for selection')
                                        : t('Cannot edit parts from library')
                                    : ''
                            }
                            arrow
                        >
                            <FormControl
                                color="primary"
                                size="small"
                                fullWidth
                            >
                                <InputLabel id="select-itemMaterial-label">{t('Material')}</InputLabel>
                                <Select
                                    disabled={!itemCuttingTechnology || !materialOptions?.length || isPartFromLibrary}
                                    id="select-itemMaterial"
                                    inputProps={{ 'data-testid': 'order-item-material' }}
                                    label={t('Material')}
                                    labelId="select-itemMaterial-label"
                                    value={itemMaterial}
                                    onChange={handleMaterialChange}
                                >
                                    {materialOptions
                                        ? materialOptions?.map((material) => (
                                              <MenuItem
                                                  key={material.materialId}
                                                  value={material.materialId}
                                              >
                                                  {material.materialName}
                                              </MenuItem>
                                          ))
                                        : null}
                                </Select>
                            </FormControl>
                        </TbxTooltip>
                    </Box>

                    {/* THICKNESS */}
                    <Box
                        sx={{
                            alignItems: 'center',
                            display: 'flex',
                            flexDirection: 'row',
                            gap: 1,
                            justifyContent: 'flex-end',
                            width: '100%',
                        }}
                    >
                        <TbxTooltip
                            title={
                                isPartFromLibrary
                                    ? itemMaterial == '' || itemThickness == ''
                                        ? t('Material for Part Library Part is no longer available for selection')
                                        : t('Cannot edit parts from library')
                                    : ''
                            }
                            arrow
                        >
                            <FormControl
                                color="primary"
                                disabled={!thicknessOptions?.length}
                                size="small"
                                fullWidth
                            >
                                <InputLabel id="select-itemThickness-label">{t('Thickness')}</InputLabel>
                                <Select
                                    disabled={!itemMaterial || !thicknessOptions?.length || isPartFromLibrary}
                                    id="select-itemThickness"
                                    inputProps={{ 'data-testid': 'order-item-thickness' }}
                                    label={t('Thickness')}
                                    labelId="select-itemThickness-label"
                                    SelectDisplayProps={{ style: { textAlign: 'right' } }}
                                    value={thicknessSelectValue}
                                    onChange={handleThicknessChange}
                                >
                                    {thicknessOptions
                                        ? thicknessOptions?.map((option) => (
                                              <MenuItem
                                                  key={option.value}
                                                  sx={{ justifyContent: 'flex-end' }}
                                                  value={option.value}
                                              >
                                                  {getThicknessDisplay(option, (value) =>
                                                      formatNumber(value, locale, useImperialUnits)
                                                  )}{' '}
                                                  {units}
                                              </MenuItem>
                                          ))
                                        : null}
                                </Select>
                                {hasIBeamOption ? <FormHelperText>{t('web / flange')}</FormHelperText> : null}
                            </FormControl>
                        </TbxTooltip>
                    </Box>
                </>
            )}

            {/* GRAIN DIRECION */}
            {showGrainDirection && !('profile' in orderItem) ? (
                <Box
                    sx={{
                        alignItems: 'center',
                        display: 'flex',
                        flexDirection: 'row',
                        justifyContent: 'space-between',
                        width: '100%',
                    }}
                >
                    <FormControl
                        color="primary"
                        size="small"
                        fullWidth
                    >
                        <InputLabel id="select-itemGrainDirection-label">{t('Grain direction')}</InputLabel>
                        <Select
                            data-testid="order-item-grain-direction"
                            id="select-itemGrainDirection"
                            label={t('Grain direction')}
                            labelId="select-itemGrainDirection-label"
                            value={itemGrainDirection}
                            onChange={handleGrainDirectionChange}
                        >
                            {grainDirectionOptions.map((direction) => (
                                <MenuItem
                                    key={`direction-${direction.value}`}
                                    value={direction.value}
                                >
                                    {direction.label}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Box>
            ) : null}

            {/* QUANTITY */}
            <Box
                sx={{
                    alignItems: 'center',
                    display: 'flex',
                    flexDirection: 'row',
                    justifyContent: 'space-between',
                    width: '100%',
                }}
            >
                <NumericFormat
                    allowNegative={false}
                    customInput={TextField}
                    decimalSeparator={currencyFormat.decimal}
                    error={quantityError}
                    helperText={quantityError ? t('Quantity cannot be less than 1') : null}
                    label={t('Quantity')}
                    placeholder="0"
                    size="small"
                    slotProps={{
                        htmlInput: {
                            style: { textAlign: 'right' },
                            'data-testid': 'order-item-quantity',
                        },
                    }}
                    sx={classes.formControl_medium}
                    thousandSeparator={currencyFormat.group}
                    value={itemQuantity}
                    fullWidth
                    onValueChange={handleQuantityChange}
                />
            </Box>
            {openNotesModal ? (
                <ItemNotesModal
                    handleUpdate={handleUpdateOrderItem}
                    key={orderItem?.id}
                    open={openNotesModal}
                    orderItem={orderItem}
                    onClose={hideNotesModal}
                />
            ) : null}
        </Box>
    )
}

ItemSettings.propTypes = {
    orderItemId: PropTypes.string.isRequired,
}

export default memo(ItemSettings)
